import { type UIViewInjectedProps, UISref, UIView } from '@uirouter/react';
import { Descriptions, Empty, Table } from 'antd';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { api } from '@/api';
import { Drawer } from '@/components/drawer';
import { PlotPolygonsMap } from '@/components/map/PlotPolygonsMap';
import { farmStore } from '@/stores';

function formatFarmerLabel(
  farmer: { farmerId?: string; firstName?: string; lastName?: string } | null | undefined,
  fallbackId?: number,
): string {
  if (farmer) {
    const name = [farmer.firstName, farmer.lastName].filter(Boolean).join(' ').trim();
    const parts = [farmer.farmerId, name].filter(Boolean);
    return parts.length > 0 ? parts.join(' · ') : '—';
  }
  if (fallbackId != null) {
    return `#${fallbackId}`;
  }
  return '—';
}

const plotColumns = [
  { title: <FormattedMessage id="farmer.farmer-plot.plotId" />, dataIndex: 'plotId', key: 'plotId', width: 120 },
  {
    title: <FormattedMessage id="farmer.farmer-plot.plotAreaHa" />,
    dataIndex: 'plotAreaHa',
    key: 'plotAreaHa',
    width: 120,
  },
  { title: <FormattedMessage id="farmer.farmer-plot.latitude" />, dataIndex: 'latitude', key: 'latitude', width: 100 },
  {
    title: <FormattedMessage id="farmer.farmer-plot.longitude" />,
    dataIndex: 'longitude',
    key: 'longitude',
    width: 100,
  },
];

export const FarmView = observer(({ transition }: UIViewInjectedProps) => {
  const farmId = +transition.router.globals.params?.farmId;
  const [plots, setPlots] = useState<cyrian.api.farmPlots.FarmPlot[]>([]);
  const [plotsLoading, setPlotsLoading] = useState(false);

  const farm = farmStore.items.find((f) => f.id === farmId);

  useEffect(() => {
    if (farmId && !farm) {
      api.farms
        .getOne(farmId)
        .source.then(({ data }) => farmStore.addItem(data))
        .catch(() => {});
    }
  }, [farmId, farm]);

  useEffect(() => {
    if (!farm?.farmerId || !farm?.id) return;
    setPlotsLoading(true);
    api.farmers
      .getPlots(farm.farmerId, { farmId: farm.id, limit: 500 })
      .source.then((res: any) => {
        const data = res?.data;
        setPlots(Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : []);
      })
      .catch(() => setPlots([]))
      .finally(() => setPlotsLoading(false));
  }, [farm?.farmerId, farm?.id]);

  const onClose = () => {
    transition.router.stateService.go('base-layout.farms');
  };

  const title = farm ? `Farm ${farm.farmId ?? farm.id}` : `Farm #${farmId}`;
  const item = farm ?? ({} as cyrian.api.farms.Farm);
  const hasPolygons = plots.some((p) => p.polygon);

  return (
    <>
      <Drawer onClose={onClose} visible={true} title={title}>
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label={<FormattedMessage id="farms.farmId" />}>
            {item.farmId?.trim() ? item.farmId : '—'}
          </Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage id="farms.farmer" />}>
            {item.farmerId != null ? (
              <UISref to="base-layout.farmers.view" params={{ farmerId: item.farmerId }}>
                <button type="button" className="ant-btn ant-btn-link ant-btn-sm p-0">
                  {formatFarmerLabel(item.farmer, item.farmerId)}
                </button>
              </UISref>
            ) : (
              formatFarmerLabel(item.farmer, item.farmerId)
            )}
          </Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage id="farms.product" />}>{item.product ?? '—'}</Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage id="farms.village" />}>{item.village ?? '—'}</Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage id="farms.district" />}>{item.district ?? '—'}</Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage id="farms.region" />}>{item.region ?? '—'}</Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage id="farmers.totalFarmAreaHa" />}>
            {item.totalFarmAreaHa != null ? item.totalFarmAreaHa : '—'}
          </Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage id="farmers.surfaceAreaHa" />}>
            {item.surfaceAreaHa != null ? item.surfaceAreaHa : '—'}
          </Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage id="farms.plotsCount" />}>
            {item.plotsCount != null ? item.plotsCount : '—'}
          </Descriptions.Item>
        </Descriptions>
        <div style={{ marginTop: 16 }}>
          <h4>
            <FormattedMessage id="farmers.plotsTab" />
          </h4>
          <Table
            rowKey="id"
            columns={plotColumns}
            dataSource={plots}
            loading={plotsLoading}
            pagination={false}
            size="small"
            scroll={{ x: 450 }}
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <h4>
            <FormattedMessage id="farms.polygonsMap" />
          </h4>
          {hasPolygons ? (
            <PlotPolygonsMap plots={plots} />
          ) : (
            <Empty description={<FormattedMessage id="farms.noPolygons" />} />
          )}
        </div>
      </Drawer>
      <UIView />
    </>
  );
});
